import {useState} from "react";
import {Trash2} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {Input} from "@/components/ui/input.tsx";
import type {DungeonFormOutput} from "@/dungeons/admin/form/DungeonFormSchema.ts";
import type {LevelGeometryDocument, LevelTransition} from "@/dungeons/model/DungeonGeometry.ts";
import {defaultTransitionArrivalImagePosition} from "@/dungeons/geometry/TransitionArrival.ts";
import TransitionArrivalPicker from "./TransitionArrivalPicker.tsx";

type Level = DungeonFormOutput["levels"][number];

const kinds: Array<{value: LevelTransition["kind"]; label: string}> = [
    {value: "STAIRS_UP", label: "Treppe aufwärts"},
    {value: "STAIRS_DOWN", label: "Treppe abwärts"},
    {value: "LADDER", label: "Leiter"},
    {value: "ELEVATOR", label: "Aufzug"},
    {value: "OTHER", label: "Anderer Übergang"},
];

export default function TransitionInspector({transition, sourceLevelId, sourceGeometry, levels, onChange, onDelete}: {
    transition: LevelTransition;
    sourceLevelId: string;
    sourceGeometry: LevelGeometryDocument;
    levels: Level[];
    onChange: (changes: Partial<Pick<LevelTransition,
        "kind" | "label" | "targetLevelId" | "arrivalImagePosition">>) => void;
    onDelete: () => void;
}) {
    const [draftLabel, setDraftLabel] = useState<string>();
    const [picking, setPicking] = useState(false);
    const label = draftLabel ?? transition.label ?? "";
    const target = levels.find(level => level.id === transition.targetLevelId);
    const arrival = transition.arrivalImagePosition;

    return <div className="space-y-3 text-sm">
        <p className="font-medium">Übergangsfläche · {transition.points.length} Punkte</p>
        <label className="block space-y-1">Art
            <select value={transition.kind} onChange={event => onChange({kind: event.target.value as LevelTransition["kind"]})}
                className="w-full rounded-md border border-input bg-surface p-2">
                {kinds.map(kind => <option key={kind.value} value={kind.value}>{kind.label}</option>)}
            </select>
        </label>
        <label className="block space-y-1">Name
            <Input value={label} maxLength={160} placeholder="Optional"
                onChange={event => setDraftLabel(event.target.value)}
                onBlur={() => {onChange({label: label.trim() || null}); setDraftLabel(undefined);}}
                onKeyDown={event => {if (event.key === "Enter") event.currentTarget.blur();}}/>
        </label>
        <label className="block space-y-1">Ziel-Level
            <select value={transition.targetLevelId ?? ""} onChange={event => {
                const nextTarget = levels.find(level => level.id === event.target.value);
                if (!nextTarget) {
                    onChange({targetLevelId: null, arrivalImagePosition: null});
                    return;
                }
                onChange({targetLevelId: nextTarget.id, arrivalImagePosition:
                    defaultTransitionArrivalImagePosition(transition.points, sourceGeometry, nextTarget.geometry)});
            }}
                className="w-full rounded-md border border-input bg-surface p-2">
                <option value="">Noch nicht verknüpft</option>
                {levels.map(level => <option key={level.id} value={level.id}>
                    {level.name}{level.id === sourceLevelId ? " (aktuelles Level)" : ""}
                </option>)}
            </select>
        </label>
        {target && <>
            <Button type="button" size="sm" variant="outline" onClick={() => setPicking(true)}>
                Ankunftspunkt ändern
            </Button>
            {arrival && <p className="text-muted-foreground">Ankunft: {arrival.x.toFixed(1)} / {arrival.y.toFixed(1)} Bildpixel</p>}
            <TransitionArrivalPicker key={target.id} level={target} position={arrival} open={picking}
                onOpenChange={setPicking} onPick={point => {
                    onChange({arrivalImagePosition: point});
                    setPicking(false);
                }}/>
        </>}
        {transition.targetLevelId && <Button type="button" size="sm" variant="outline" onClick={() => {
            onChange({targetLevelId: null, arrivalImagePosition: null});
        }}>Zielverknüpfung entfernen</Button>}
        {!transition.targetLevelId && <p className="text-amber-400">Noch kein Ziel festgelegt; im Encounter nicht nutzbar.</p>}
        <Button type="button" size="sm" variant="destructive" onClick={onDelete}>
            <Trash2/> Übergang löschen
        </Button>
    </div>;
}
